import { Link } from "@tanstack/react-router";
import { ArrowRight, Phone, HelpCircle, ChevronRight } from "lucide-react";
import { SiteLayout } from "@/components/SiteLayout";
import { PageHero } from "@/components/PageHero";
import { type AnswerEntry, getRelatedAnswers } from "@/lib/answersData";
import { SCHEMA_PERSON_ID, SCHEMA_ORG_ID, buildSpeakableSchema } from "@/lib/entitySchema";

const SITE_URL = "https://coloradowebimpressions.com";

export function buildAnswerHead(entry: AnswerEntry) {
  const path = `/answers/${entry.slug}`;
  const url = `${SITE_URL}${path}`;
  // Keep titles under 60 chars for SERP and social previews.
  const suffix = " | Colorado Web Impressions";
  const maxQuestionLen = 60 - suffix.length;
  const trimmedQuestion = entry.question.length > maxQuestionLen
    ? entry.question.slice(0, maxQuestionLen - 1).trimEnd() + "…"
    : entry.question;
  const title = `${trimmedQuestion}${suffix}`;
  const description = entry.shortAnswer.length > 160
    ? entry.shortAnswer.slice(0, 157) + "..."
    : entry.shortAnswer;

  const qaPage = {
    "@context": "https://schema.org",
    "@type": "QAPage",
    mainEntity: {
      "@type": "Question",
      name: entry.question,
      text: entry.question,
      answerCount: 1,
      dateCreated: entry.updated,
      datePublished: entry.updated,
      author: { "@id": SCHEMA_PERSON_ID },
      acceptedAnswer: {
        "@type": "Answer",
        text: entry.shortAnswer,
        dateCreated: entry.updated,
        datePublished: entry.updated,
        upvoteCount: 1,
        url,
        author: { "@id": SCHEMA_PERSON_ID },
      },
    },
  };

  const article = {
    "@context": "https://schema.org",
    "@type": "Article",
    mainEntityOfPage: url,
    headline: entry.question,
    description: entry.shortAnswer,
    datePublished: entry.updated,
    dateModified: entry.updated,
    image: `${SITE_URL}/logo.png`,
    author: { "@id": SCHEMA_PERSON_ID },
    publisher: { "@id": SCHEMA_ORG_ID },
    about: [entry.category, "Colorado", "Marketing"],
  };

  const faqPage = entry.relatedFaqs.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: entry.relatedFaqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  } : null;

  const breadcrumbs = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
      { "@type": "ListItem", position: 2, name: "Answers", item: `${SITE_URL}/answers` },
      { "@type": "ListItem", position: 3, name: entry.question, item: url },
    ],
  };

  const scripts: { type: string; children: string }[] = [
    { type: "application/ld+json", children: JSON.stringify(qaPage) },
    { type: "application/ld+json", children: JSON.stringify(article) },
    { type: "application/ld+json", children: JSON.stringify(breadcrumbs) },
    { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema(url, entry.question)) },
  ];
  if (faqPage) {
    scripts.push({ type: "application/ld+json", children: JSON.stringify(faqPage) });
  }

  return {
    meta: [
      { title },
      { name: "description", content: description },
      { name: "robots", content: "index, follow, max-image-preview:large" },
      { property: "og:title", content: entry.question },
      { property: "og:description", content: description },
      { property: "og:type", content: "article" },
      { property: "og:url", content: url },
      { property: "article:modified_time", content: entry.updated },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: entry.question },
      { name: "twitter:description", content: description },
    ],
    links: [{ rel: "canonical", href: url }],
    scripts,
  };
}

export function AnswerPage({ entry }: { entry: AnswerEntry }) {
  const related = getRelatedAnswers(entry.related);

  return (
    <SiteLayout>
      <PageHero
        eyebrow={`Answers · ${entry.category}`}
        title={entry.question}
        description={`Updated ${new Date(entry.updated).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })} · Honest answer from a Colorado agency that does this work daily.`}
      />

      {/* Lift-ready short answer block (QAPage schema target) */}
      <section className="bg-background py-10 lg:py-14">
        <div className="mx-auto max-w-3xl px-5 lg:px-8">
          <div className="rounded-2xl border-2 border-brand-gold/40 bg-card p-6 lg:p-8 shadow-elegant">
            <div className="flex items-start gap-3">
              <HelpCircle className="mt-1 h-5 w-5 shrink-0 text-brand-gold-deep" />
              <div className="flex-1">
                <p className="text-xs font-bold uppercase tracking-wider text-brand-gold-deep">
                  Short answer
                </p>
                <p
                  className="mt-2 text-base leading-relaxed text-foreground"
                  data-speakable
                >
                  {entry.shortAnswer}
                </p>
                {entry.bullets.length > 0 && (
                  <ul className="mt-4 space-y-2">
                    {entry.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-2 text-sm text-muted-foreground">
                        <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-brand-gold-deep" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Long-form body */}
      <section className="bg-section-alt py-12 lg:py-16">
        <div className="mx-auto max-w-3xl px-5 lg:px-8 space-y-10">
          {entry.body.map((section) => (
            <div key={section.heading}>
              <h2 className="text-2xl font-extrabold text-foreground">{section.heading}</h2>
              <div className="mt-4 space-y-4">
                {section.paragraphs.map((p, i) => (
                  <p key={i} className="text-sm leading-relaxed text-muted-foreground">
                    {p}
                  </p>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Related FAQs (FAQPage schema target) */}
      {entry.relatedFaqs.length > 0 && (
        <section className="bg-background py-12 lg:py-16">
          <div className="mx-auto max-w-3xl px-5 lg:px-8">
            <h2 className="text-2xl font-extrabold text-foreground">Follow-up questions</h2>
            <div className="mt-6 space-y-3">
              {entry.relatedFaqs.map((f, idx) => (
                <details
                  key={f.q}
                  className="group rounded-lg border border-border bg-card p-4"
                  open={idx < 2}
                >
                  <summary className="cursor-pointer text-sm font-semibold text-foreground marker:hidden">
                    {f.q}
                  </summary>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Related answers */}
      {related.length > 0 && (
        <section className="bg-section-alt py-12 lg:py-16">
          <div className="mx-auto max-w-4xl px-5 lg:px-8">
            <h2 className="text-2xl font-extrabold text-foreground">Related answers</h2>
            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              {related.map((r) => (
                <Link
                  key={r.slug}
                  to="/answers/$slug"
                  params={{ slug: r.slug }}
                  className="group rounded-lg border border-border bg-background p-5 transition-smooth hover:border-brand-gold/60"
                >
                  <p className="text-xs font-semibold uppercase tracking-wider text-brand-gold-deep">
                    {r.category}
                  </p>
                  <p className="mt-1.5 text-sm font-semibold text-foreground group-hover:text-brand-gold-deep">
                    {r.question}
                  </p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="bg-background py-12 lg:py-16">
        <div className="mx-auto max-w-3xl px-5 lg:px-8 text-center">
          <h2 className="text-2xl font-extrabold text-foreground">
            Want a real conversation about your situation?
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-sm text-muted-foreground">
            Free audit. We'll walk through what's actually working for businesses like yours in Colorado — no sales pressure, no contracts.
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/free-audit"
              className="inline-flex items-center gap-2 rounded-lg bg-brand-gold-deep px-5 py-3 text-sm font-semibold text-background transition-smooth hover:bg-brand-gold"
            >
              Get my free audit
              <ArrowRight className="h-4 w-4" />
            </Link>
            <a
              href="tel:+17194193935"
              className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-5 py-3 text-sm font-semibold text-foreground transition-smooth hover:border-brand-gold/60"
             aria-label="Call (719) 419-3935">
              <Phone className="h-4 w-4" />
              (719) 419-3935
            </a>
          </div>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3 text-xs">
            <Link to="/answers" className="text-muted-foreground underline-offset-4 hover:text-brand-gold-deep hover:underline">
              All answers →
            </Link>
            <Link to="/pricing" className="text-muted-foreground underline-offset-4 hover:text-brand-gold-deep hover:underline">
              Pricing →
            </Link>
            <Link to="/faq" className="text-muted-foreground underline-offset-4 hover:text-brand-gold-deep hover:underline">
              Full FAQ →
            </Link>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
